import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatDateTime, formatDateOnly, formatCalendarDateUTC } from "../../utils/date";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(location?.state?.order?.raw || null);
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState(null);

  const fetchOrder = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken") || "";
      const url = `${API_BASE_URL}/order/${encodeURIComponent(id)}`;
      const res = await fetch(url, { headers: { "Content-Type": "application/json", ...(token ? { token } : {}) } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // API may return object or { result: { order }} etc.
      const data = json?.result || json || null;
      setOrder(data);
    } catch (err) {
      setError(err.message || "Failed to load order");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If we had a lightweight order from the list, we still fetch authoritative details
    if (!order) fetchOrder();
  }, [id]);

  if (loading) return <div className="p-4">Loading order…</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;
  if (!order) return <div className="p-4">Order not found.</div>;

  // prefer latest API shapes
  const items = order.orderItems || order.items || [];
  const total = Number(order.final_amount ?? order.total_price ?? order.finalAmount ?? items.reduce((s, it) => s + ((Number(it.total_price ?? it.unit_price ?? it.price ?? 0)) * (it.quantity ?? it.qty ?? 1)), 0));

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Order Details</h2>
          <div className="text-sm text-gray-600">Order#: {order.order_number || order.orderId || order.id || id}</div>
          <div className="text-sm text-gray-600">Status: {order.status || order.order_status || "-"}</div>
        </div>
        <div className="flex gap-2">
          <button className="px-3 py-1 bg-gray-200 rounded" onClick={() => nav(-1)}>Back</button>
          <button className="px-3 py-1 bg-blue-600 text-white rounded" onClick={fetchOrder}>Refresh</button>
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="mb-3"><strong>Placed:</strong> {order.created_at ? formatDateTime(order.created_at) : (order.placedAt ? formatDateTime(order.placedAt) : order.date || "-")}</div>
            {order.estimate_delivery_date && (
              <div className="mb-3"><strong>Estimated Delivery:</strong> {formatCalendarDateUTC(order.estimate_delivery_date)}</div>
            )}
            {order.delivery_remark && (
              <div className="mb-3"><strong>Delivery Remark:</strong> {order.delivery_remark}</div>
            )}

            <div className="mb-3"><strong>Items:</strong></div>
            <div className="space-y-3">
              {items.map((it, idx) => {
                const product = it.product || {};
                const name = it.product_name || it.productName || product.product_name || it.name || "Product";
                const img = product?.product_media?.[0]?.url || it.image || "/placeholder.png";
                const qty = it.quantity ?? it.qty ?? 1;
                const price = Number(it.unit_price ?? it.unitPrice ?? it.total_price ?? it.price ?? 0);
                return (
                  <div key={idx} className="flex gap-3 items-center border p-2 rounded">
                    <img src={img} alt={name} className="w-20 h-20 object-cover rounded"/>
                    <div className="flex-1">
                      <div className="font-semibold">{name}</div>
                      <div className="text-sm text-gray-600">Qty: {qty}</div>
                      {product?.description && <div className="text-sm text-gray-500">{product.description}</div>}
                    </div>
                    <div className="text-right">
                      <div>₹{price}</div>
                      <div className="text-sm text-gray-600">Subtotal: ₹{(price * qty).toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <div className="mb-2"><strong>Shipping</strong></div>
            <div className="text-sm text-gray-700">
              {order.shipping_address ? (
                <div>
                  <div className="font-semibold">{order.shipping_address.name}</div>
                  <div>{order.shipping_address.address_line_1}</div>
                  {order.shipping_address.address_line_2 && <div>{order.shipping_address.address_line_2}</div>}
                  <div>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</div>
                  <div>{order.shipping_address.country} • {order.shipping_address.phone}</div>
                </div>
              ) : order.shippingAddress ? (
                <div>
                  <div>{order.shippingAddress.name}</div>
                  <div>{order.shippingAddress.line1}</div>
                  <div>{order.shippingAddress.city} {order.shippingAddress.postalCode}</div>
                </div>
              ) : <div>-</div>}
            </div>

            <hr className="my-3"/>

            <div className="mb-2"><strong>Seller</strong></div>
            <div className="text-sm text-gray-700 mb-3">
              {order.seller ? (
                <div>
                  <div className="font-semibold">{order.seller.name}</div>
                  {order.seller.profile_image?.url && <img src={order.seller.profile_image.url} alt={order.seller.name} className="w-16 h-16 object-cover rounded mt-2" />}
                </div>
              ) : <div>-</div>}
            </div>

            <div className="flex justify-between"><div className="text-gray-600">Total</div><div className="font-bold">₹{Number(total).toFixed(2)}</div></div>

            {order.history && order.history.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 font-semibold">History</div>
                <div className="text-sm text-gray-700 space-y-2">
                  {order.history.map((h) => (
                    <div key={h.id} className="border p-2 rounded">
                      <div className="text-sm"><strong>{h.status}</strong> — {h.description}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(h.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
